import React from 'react';

import { ColumnDefinition, RowDefinition, Table } from '@indigo-ag/component-module';
import { OpportunityContactRole } from '@indigo-ag/schema';
import i18next from 'i18next';
import { useParams } from 'react-router-dom';
import { getFormattedTaxIdentifier, getOpportunityContactRoleType, getTranslatedRole } from '../utils';

enum ColumnId {
  NAME = 'NAME',
  ROLE = 'ROLE',
  TIN = 'TIN'
}

interface RowShape {
  id: string;
  contactId: string;
  name: string;
  role: string;
  tin: string;
}

const columnDefinition: ColumnDefinition<RowShape, ColumnId>[] = [
  {
    id: ColumnId.NAME,
    header: i18next.t('fullName'),
    cell: ({ row: { name }, SimpleCell }) => <SimpleCell label={name} />
  },
  {
    id: ColumnId.ROLE,
    header: i18next.t('role'),
    cell: ({ row: { role }, SimpleCell }) => <SimpleCell label={role} />
  },
  {
    id: ColumnId.TIN,
    header: i18next.t('cpfNumber'),
    cell: ({ row: { tin }, SimpleCell }) => <SimpleCell label={getFormattedTaxIdentifier(tin) ?? tin} />
  }
];

interface Props {
  contactRoles: OpportunityContactRole[];
}

export const RelationshipsTable = ({ contactRoles }: Props) => {
  const { opportunityId } = useParams<{ contactId: string; opportunityId?: string }>();
  const rowDefinition: RowDefinition<RowShape, ColumnId> = {
    rowId: ({ row: { id } }) => id,
    actions: ({ ActionLinkText, row: { contactId } }) => (
      <a href={`/br-growers/${opportunityId}/contacts/${contactId}`}>
        <ActionLinkText>{i18next.t('viewContact')}</ActionLinkText>
      </a>
    ),
    actionsLocation: 'inline'
  };
  const data: RowShape[] = contactRoles.map(({ id, contactId, isPrimary, role, contact: { name, taxIdentifier } }) => ({
    id,
    contactId,
    role: isPrimary ? i18next.t('primaryContact') : getTranslatedRole(getOpportunityContactRoleType(role) ?? role),
    name,
    tin: taxIdentifier ?? '-'
  }));

  return (
    <Table
      columnDefinition={columnDefinition}
      data={data}
      header={i18next.t('relationships')}
      label={'relationships'}
      rowDefinition={rowDefinition}
      tableStyle="inline"
    />
  );
};
